import { useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import {
  useAuditS3ConsistencyMutation,
  useDeleteOrphanedRecordsMutation,
} from '../adminTools.mutations';

export interface AuditResult {
  success: boolean;
  total_checked: number;
  valid_records: number;
  orphaned_count: number;
  orphaned_records: Array<{ flight_id: string; s3_key: string }>;
}

export interface UseS3ConsistencyAuditReturn {
  // State
  auditLoading: boolean;
  auditResult: AuditResult | null;
  deleteLoading: boolean;

  // Computed
  hasOrphanedRecords: boolean;
  orphanedCount: number;

  // Actions
  runAudit: () => Promise<void>;
  deleteOrphanedRecords: () => Promise<void>;
  clearResults: () => void;
}

export function useS3ConsistencyAudit(): UseS3ConsistencyAuditReturn {
  const { showError, showSuccess } = useErrorHandler();

  const auditMutation = useAuditS3ConsistencyMutation();
  const deleteMutation = useDeleteOrphanedRecordsMutation();
  const auditResult = auditMutation.data ?? null;

  const runAudit = useCallback(async () => {
    try {
      const result = await auditMutation.mutateAsync();
      if (result.orphaned_count > 0) {
        showError(
          `Found ${result.orphaned_count} orphaned records (DB entries with missing S3 files)`,
          'S3 Audit Warning'
        );
      } else {
        showSuccess(`All ${result.valid_records} records have valid S3 files`);
      }
    } catch (error) {
      showError(error, 'Failed to audit S3 consistency');
    }
  }, [auditMutation, showError, showSuccess]);

  const deleteOrphanedRecords = useCallback(async () => {
    if (!auditResult?.orphaned_records?.length) return;

    try {
      const flightIds = auditResult.orphaned_records.map((r) => r.flight_id);
      const result = await deleteMutation.mutateAsync(flightIds);
      if (result.deleted_count > 0) {
        showSuccess(`Deleted ${result.deleted_count} orphaned records`);
        // Clear the audit result after successful deletion
        auditMutation.reset();
      }
      if (result.failed_count > 0) {
        showError(`Failed to delete ${result.failed_count} records`);
      }
    } catch (error) {
      showError(error, 'Failed to delete orphaned records');
    }
  }, [auditResult, auditMutation, deleteMutation, showError, showSuccess]);

  const clearResults = useCallback(() => {
    auditMutation.reset();
  }, [auditMutation]);

  return {
    auditLoading: auditMutation.isPending,
    auditResult,
    deleteLoading: deleteMutation.isPending,
    hasOrphanedRecords: (auditResult?.orphaned_count ?? 0) > 0,
    orphanedCount: auditResult?.orphaned_count ?? 0,
    runAudit,
    deleteOrphanedRecords,
    clearResults,
  };
}
